import React, {
  ChangeEvent,
  ChangeEventHandler,
  FormEventHandler,
  useState,
} from 'react';
import {
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row,
} from 'reactstrap';
import Etiquetas from '../../components/etiquetas';
import Select from 'react-select';
import {tag, tagFront} from '../../types/general-types';
import {
  useGetPlansRequest,
  useGetSpecialityListRequest,
} from '../../api/registerLink';
import FormSelectInput from '../../components/FormSelectInput';
import FormTextInput from '../../components/FormTextInput';
import {DestinationType, useDestination} from '../../hooks/useDestination';
import {useSendCustomNotification} from '../../api/customNotification';
import {Store} from 'react-notifications-component';

type FormValues = {
  title: string;
  content: string;
  notification: 'ALL' | 'TAG' | undefined;
  tags: tagFront[];
};

type FormErrors = Partial<Record<keyof FormValues, string | undefined>>;

type ResponseData = {
  id: string;
  label: string;
};

const baseFields = {
  title: '',
  content: '',
} as const;

const fields = {
  ...baseFields,
  notification: undefined,
  tags: [],
};

const errors = {
  ...baseFields,
  notification: '',
  tags: '',
} as const;

const pushOptions = [
  {value: 'ALL', label: 'A todos los Usuarios'},
  {value: 'TAG', label: 'Solo Usuarios con etiquetas'},
] as const;

const tiposDestinos = [
  {id: 'instantVideo', label: 'Consulta Inmediata'},
  {id: 'videoAppointment', label: 'Agendar Consulta'},
  {id: 'plan', label: 'Suscribirse a un plan'},
];
const formasDePago = [
  {id: 'pagoUnico', label: 'Pago Único'},
  {id: 'suscribirPlan', label: 'Subscribir a un plan'},
];

const EnviarNotificaciones = () => {
  const [formValues, setFormValues] = useState<FormValues>(fields);
  const [formErrors, setFormErrors] = useState<FormErrors>(errors);
  const [selectedPushOption, setSelectedPushOption] = useState<
    {value: 'ALL'; label: string} | {value: 'TAG'; label: string}
  >();
  const [tags, setTags] = useState<tagFront[]>([]);
  const [destinoEstablecido, setDestinoEstablecido] = useState<boolean>(false);
  const [tipoDestino, setTipoDestino] = useState<DestinationType>();
  const [especialidades, setEspecialidades] = useState<ResponseData[] | null>(
    null,
  );
  const [planes, setPlanes] = useState<ResponseData[] | null>(null);
  const [especialidadSeleccionada, setEspecialidadesSeleccionada] =
    useState<number>();
  const [formaPagoSeleccionada, setFormaPagoSeleccionada] = useState<string>();
  const [planSeleccionado, setPlanSeleccionado] = useState('');

  const getSpecialityList = useGetSpecialityListRequest();
  const getPlans = useGetPlansRequest();

  const handleTipoDestinoChange: ChangeEventHandler<
    HTMLInputElement
  > = async (e: {target: {value: React.SetStateAction<string>}}) => {
    let tipoDestino = e.target.value as DestinationType;
    setTipoDestino(tipoDestino);
    if (tipoDestino === 'instantVideo' || tipoDestino === 'videoAppointment') {
      setPlanes(null);
      const specialities = await getSpecialityList(tipoDestino);
      const mappedSpecialities = specialities.map((c) => ({
        id: c.id.toString(),
        label: c.description,
      }));
      setEspecialidades(mappedSpecialities);
    } else {
      if (tipoDestino === 'plan') {
        setEspecialidades(null);
        const plans = await getPlans();
        const mappedPlanes = plans.map((c) => ({
          id: c.id.toString(),
          label: c.name,
        }));
        setPlanes(mappedPlanes);
      }
    }
  };

  const handleFormaDePago: ChangeEventHandler<HTMLInputElement> = async (e: {
    target: {value: React.SetStateAction<string>};
  }) => {
    let formaDePago = (e.target.value as string) ?? undefined;
    setFormaPagoSeleccionada(formaDePago);
    if (formaDePago === 'suscribirPlan') {
      const plans = await getPlans();
      const mappedPlanes = plans.map((c) => ({
        id: c.id.toString(),
        label: c.name,
      }));
      setPlanes(mappedPlanes);
    }
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const {name, value} = event.target;
    setFormValues((formValues) => ({
      ...formValues,
      [name]: value,
    }));
    setFormErrors((formErrors) => ({
      ...formErrors,
      [name]: '',
    }));
  };

  const sendCustomNotification = useSendCustomNotification();
  const getDestination = useDestination();

  const handleSubmit: FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    console.log(especialidadSeleccionada);
    let tieneErrores = false;
    const errores: FormErrors = {};
    if (formValues.notification === 'TAG' && !formValues.tags.length) {
      tieneErrores = true;
      errores['tags'] = `El campo tags es obligatorio`;
    }
    Object.entries(formValues).forEach(([clave, valor]) => {
      if (!valor) {
        tieneErrores = true;
        errores[clave as keyof FormValues] = `El campo ${clave} es obligatorio`;
      }
    });
    if (tieneErrores) {
      setFormErrors(formErrors);
    } else {
      const tagsTransformed = tags.map((t) => ({
        ...t,
        description: t.text,
      })) as tag[];
      const deepLink = tipoDestino
        ? getDestination({
            type: tipoDestino,
            speciality: especialidadSeleccionada,
            planId: planSeleccionado,
          })
        : undefined;

      const content = {
        title: formValues.title,
        content: formValues.content,
        deepLink,
      };

      await sendCustomNotification(
        content,
        formValues.notification!,
        tagsTransformed,
      );

      Store.addNotification({
        title: 'Excelente',
        message: 'La notificación push ha sido enviada correctamente',
        type: 'success',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animate__animated', 'animate__fadeIn'],
        animationOut: ['animate__animated', 'animate__fadeOut'],
        dismiss: {
          duration: 5000,
          onScreen: true,
        },
      });

      setFormValues(fields);
      setSelectedPushOption(undefined);
      setDestinoEstablecido(false);
      setTipoDestino(undefined);
      setEspecialidadesSeleccionada(undefined);
      setFormaPagoSeleccionada(undefined);
    }
  };

  return (
    <Container className="flex-grow-1 pt-4">
      <h2 className="h2 py-4">Enviar Notificación</h2>
      <Form onSubmit={handleSubmit}>
        <FormTextInput
          label="Título"
          id="title"
          value={formValues.title}
          evento={handleInputChange}
          error={formErrors.title}
        />

        <FormTextInput
          label="Descripción"
          id="content"
          value={formValues.content}
          evento={handleInputChange}
          error={formErrors.content}
        />

        <FormGroup className="pt-4">
          <Label>Notificación Push:</Label>
          <Select
            value={selectedPushOption}
            onChange={(newValue) => {
              setSelectedPushOption(newValue ?? pushOptions[0]);
              setFormValues((formValues) => ({
                ...formValues,
                notification: newValue?.value ?? pushOptions[0].value,
              }));
            }}
            options={pushOptions}
          />
        </FormGroup>
        {selectedPushOption?.value === 'TAG' && (
          <Row>
            <Col className="mb-2">
              <Etiquetas tags={tags} setEtiquetas={setTags} />
            </Col>
          </Row>
        )}
        <FormGroup className="pt-4">
          <Label check className="mb-3">
            <Input
              type="checkbox"
              checked={destinoEstablecido}
              onChange={(e) => {
                setDestinoEstablecido(e.target.checked);
              }}
            />{' '}
            Establecer destino
          </Label>

          {destinoEstablecido && (
            <>
              <FormSelectInput
                label="Tipo de destino"
                id="tipoDestino"
                options={tiposDestinos}
                evento={handleTipoDestinoChange}
                error={undefined}
                placeholder=" "
              />
              {tipoDestino !== 'plan' && especialidades && (
                <>
                  <FormSelectInput
                    label="Especialidades"
                    id="especialidades"
                    options={especialidades}
                    evento={(e) => {
                      setEspecialidadesSeleccionada(Number(e.target.value));
                    }}
                    error={undefined}
                  />
                  <FormSelectInput
                    label="Forma de Pago"
                    id="formadePago"
                    options={formasDePago}
                    placeholder=" "
                    evento={(e) => {
                      handleFormaDePago(e);
                    }}
                    error={undefined}
                  />
                </>
              )}
              {!!planes?.length &&
                (tipoDestino === 'plan' ||
                  formaPagoSeleccionada === 'suscribirPlan') && (
                  <FormSelectInput
                    label="Planes"
                    id="planes"
                    options={planes}
                    placeholder=" "
                    evento={(e) => {
                      setPlanSeleccionado(e.target.value);
                    }}
                    error={undefined}
                  />
                )}
            </>
          )}
        </FormGroup>
        <Button className="my-4" type="submit">
          Enviar
        </Button>
      </Form>
    </Container>
  );
};

export default EnviarNotificaciones;
